import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Sprout, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    const to = loc.state?.from?.pathname || "/dashboard";
    nav(to, { replace: true });
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:flex relative flex-col justify-end p-12 text-white overflow-hidden grain-overlay"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(27,67,50,0.55), rgba(17,28,21,0.9)), url('https://images.pexels.com/photos/33786744/pexels-photo-33786744.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-2 mb-6">
            <span className="h-10 w-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center">
              <Sprout className="h-6 w-6" />
            </span>
            <div className="font-display font-bold text-xl">PRONAF Projects</div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight">
            Projetos PRONAF <span className="text-accent">Grupo A</span>
            <br /> elaborados com precisão.
          </h1>
          <p className="mt-4 text-white/80 text-sm">
            Preencha o formulário guiado, deixe que os cálculos de financiamento, cronograma de
            reembolso e fluxo de caixa sejam feitos automaticamente, e gere o PDF oficial pronto
            para impressão.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="inline-flex items-center gap-2 lg:hidden mb-6">
              <span className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
                <Sprout className="h-5 w-5" />
              </span>
              <div className="font-display font-bold text-primary">PRONAF Projects</div>
            </div>
            <h2 className="font-display text-3xl font-bold">Entrar</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Acesse sua conta para continuar elaborando seus projetos.
            </p>
          </div>

          {err && (
            <Alert variant="destructive" data-testid="login-error">
              <AlertDescription>{err}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Entrar
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            Ainda não tem conta?{" "}
            <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="go-register-link">
              Criar conta
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
